import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { showLoadingScreen } from 'a/hub/actions/metaGameActions';
import { openEreader } from 'a/eReader/redux/actions/ereader';
import { reportNavigation } from 'a/hub/actions/metagameSearchlight';
import GameIds from 'a/hub/data/GameIds';
import { CURIOSO_TYPES_MAP } from 'root/activities/hub/data/constants';
import {
  DESTINATION_GROUP,
  NAVIGATION_ORIGIN,
  NAVIGATION_TRIGGER_TYPE,
} from 'root/lib/SearchlightDispatcher';
import { autoCompleteEreaderSession, setWorkshopStepAsInProgress } from 'root/activities/hub/actions/workshopActions';
import {
  getSelectedCuriosoType,
  getSelectedBookFromCollection,
} from 'root/activities/hub/selectors/workshopSelectors';
import CuriosoHoloBeast from '../../../svgs/modals/eReader/eReaderLaunch/CuriosoHoloBeast.svg';
import CuriosoHoloBird from '../../../svgs/modals/eReader/eReaderLaunch/CuriosoHoloBird.svg';
import CuriosoHoloCat from '../../../svgs/modals/eReader/eReaderLaunch/CuriosoHoloCat.svg';
import useWorkshopSfx from '../../../hooks/useWorkshopSfx';
import {
  coolCuriosoFilter,
  greenCuriosoFilter,
  greenFilter,
  orangeFilter,
  purpleCuriosoFilter,
  purpleFilter,
  warmCuriosoFilter,
} from '../../SVGFilter/filters';
import SVGFilter from '../../SVGFilter/SVGFilter';
import {
  ModalPanel,
  TitleRow,
  CuriosoIcon,
  Background,
  Burst,
  ChapterNumber,
  CloseModalButton,
  CuriosoCircle,
  CuriosoContainer,
  Decoline,
  GoButton,
  ModalText,
  TopIcon,
} from './styledComponents';
import BurstIcon from './svgs/BurstIcon.svg';
import DecolineMiddle from './svgs/DecolineMiddle.svg';
import SmallBackground from '../svgs/Common/SmallBackground.svg';
import TopIconEreader from './svgs/TopIconEreader.svg';
import { AutoCompleteButton, ModalContainer } from '../sharedComponents';

const curiosoSkins = {
  [CURIOSO_TYPES_MAP.furry]: CuriosoHoloBeast,
  [CURIOSO_TYPES_MAP.feathery]: CuriosoHoloBird,
  [CURIOSO_TYPES_MAP.scaly]: CuriosoHoloCat,
};

const getFilter = (curiosoColor) => {
  switch (curiosoColor) {
    case 'green':
      return greenFilter;
    case 'RewardChoiceBook4':
    case 'orange':
      return orangeFilter;
    case 'warm':
      return warmCuriosoFilter;
    case 'RewardChoiceBook11':
    case 'purple':
      return purpleCuriosoFilter;
    case 'RewardChoiceBook5':
    case 'cool':
      return coolCuriosoFilter;
    case 'RewardChoiceBook15':
    case 'cirton':
      return greenCuriosoFilter;
    default:
      return purpleFilter;
  }
};

const EreaderLaunchModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const curiosoSkin = useSelector(getSelectedCuriosoType);
  const questId = useSelector(({ workshopReducer: { currentQuestId } }) => currentQuestId);
  const {
    eReader: { bookName, chapter: chapterNumber, bookId, skillId },
  } = useSelector(({ workshopReducer: { workshop } }) => workshop[`quest${questId}`]);

  const [iconWidth, setIconWidth] = React.useState(0);

  const { playTap, playSfxFromMap, audioMap } = useWorkshopSfx();

  useEffect(() => {
    playSfxFromMap(audioMap.ereader_launch);
    const timeout = setTimeout(() => setIconWidth(150), 200);
    return () => clearTimeout(timeout);
  }, []);

  const selectedBook = useSelector(getSelectedBookFromCollection);

  const launchEreader = async () => {
    const gameLaunchedFrom = {
      location: undefined,
      categoryId: '',
      questId,
    };
    const navigationParams = {
      destination: bookId,
      destinationGroup: DESTINATION_GROUP.EREADER,
      origin: NAVIGATION_ORIGIN.WORKSHOP,
      trigger: 'eReaderLaunchModal',
      triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
    };
    dispatch(reportNavigation({ ...navigationParams }));
    dispatch({
      gameId: GameIds.METAGAME,
      type: 'SEARCHLIGHT_DISPATCH_EVENT',
      payload: {
        gameId: GameIds.METAGAME,
        application: {
          gameId: GameIds.E_READER,
          eventType: 'APP_LAUNCHED',
          details: {
            questId,
            isQuestWorkflow: true,
          },
        },
      },
    });
    dispatch(showLoadingScreen());
    await dispatch(setWorkshopStepAsInProgress(true));
    dispatch(
      openEreader({
        isQuestWorkflow: true,
        navigationParams,
        gameLaunchedFrom,
        params: { isWorkshop: true, game: bookId, isChallengeQuest: false, skillId },
        bookInfo: {
          bookGameId: bookId,
          chapter: chapterNumber || '',
        },
      }),
    );
  };

  const onCloseButtonClick = () => {
    playTap(true);
    closeModal();
  };

  const onGoButtonClick = () => {
    playTap();
    closeModal();
    launchEreader();
  };

  return (
    <ModalContainer
      role="dialog"
      aria-modal="true"
      aria-labelledby="ereader-launch-modal-title"
      data-testid="ereader-launch-modal"
      data-dd-action-name="ereader-launch-modal"
    >
      <ModalPanel>
        <CloseModalButton
          onClick={onCloseButtonClick}
          data-testid="ereader-launch-modal-close-button"
          data-dd-action-name="ereader-launch-modal:close"
        />
        <Background src={SmallBackground} alt="" />
        <TopIcon src={TopIconEreader} alt="" />
        <TitleRow>
          <FadeInTitle
            id="ereader-launch-modal-title"
            data-testid="ereader-launch-modal-book-title"
          >
            {bookName}
          </FadeInTitle>
          <CuriosoIcon>
            <Burst src={BurstIcon} alt="" />
            <CuriosoCircle curiosoSkin={curiosoSkin} />
            <CuriosoContainer style={{ width: iconWidth }} data-testid="ereader-modal-curioso-avatar">
              <SVGFilter
                id={`curioso-${curiosoSkin}`}
                filter={getFilter(selectedBook)}
                image={curiosoSkins[curiosoSkin]}
              />
            </CuriosoContainer>
          </CuriosoIcon>
        </TitleRow>
        <Decoline src={DecolineMiddle} alt="" />
        {!!chapterNumber && (
          <FadeInChapterNumber data-testid="ereader-modal-book-chapter">
            {chapterNumber.split(/([0-9]+)/).join(' ')}
          </FadeInChapterNumber>
        )}
        <GoButton onClick={onGoButtonClick} ariaLabel="Open Book" actionName="ereader-launch-modal:go" />

      </ModalPanel>
      <AutoCompleteButton onClick={() => {
        dispatch(autoCompleteEreaderSession(questId));
        closeModal();
      }}
      />
    </ModalContainer>
  );
};

EreaderLaunchModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default EreaderLaunchModal;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const FadeInTitle = styled(ModalText)`
  animation: ${fadeIn} 2s forwards;
`;

const FadeInChapterNumber = styled(ChapterNumber)`
  opacity: 0;
  animation: ${fadeIn} 2s forwards;
  animation-delay: 0.5s;
`;
